import { Empty, Popconfirm } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import type { PropRuntimeGraph } from "../../game/assets/shared/types/PropRuntimeGraph";
import type { BattleManager } from "../../game/assets/pri_wqs/scripts/core/BattleManager";
import type {
  PropRuntimeEvent,
  RuntimePropInstance,
} from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import {
  InstanceCard,
  InstanceRemoveControl,
  QuietBadge,
  SandboxSection,
  SandboxSectionHeader,
  ScrollableInstanceGrid,
} from "./styles";
import { PropCard } from "./PropCard";

export interface MaterialLibraryPanelProps {
  manager?: BattleManager;
  catalog?: PropRuntimeGraph[];
  selectedId: string;
  onSelect: (item?: RuntimePropInstance) => void;
  removeDisabled?: boolean;
}

export function MaterialLibraryPanel({
  manager,
  catalog,
  selectedId,
  onSelect,
  removeDisabled,
}: MaterialLibraryPanelProps) {
  const [items, setItems] = useState<RuntimePropInstance[]>([]);

  useEffect(() => {
    setItems([]);
    if (!manager) return undefined;

    const eventBus = manager.getEventBus();
    const addOrUpdateItem = (event: PropRuntimeEvent) => {
      if (!event.item || !isMaterialItem(event.item)) return;
      if (!event.item.alive) {
        setItems((prev) => removeItem(prev, event.item!.id));
        return;
      }
      setItems((prev) => upsertItem(prev, event.item!));
    };
    const removeEventItem = (event: PropRuntimeEvent) => {
      if (!event.item || !isMaterialItem(event.item)) return;
      setItems((prev) => removeItem(prev, event.item!.id));
    };

    const unsubs = [
      eventBus.on("propAcquired", addOrUpdateItem),
      eventBus.on("propRemoved", removeEventItem),
    ];

    return () => {
      unsubs.forEach((unsubscribe) => unsubscribe());
    };
  }, [manager]);

  return (
    <SandboxSection $fill>
      <SandboxSectionHeader>
        <span>原料库</span>
        <QuietBadge count={items.length} overflowCount={999} />
      </SandboxSectionHeader>
      {items.length ? (
        <ScrollableInstanceGrid>
          {items.map((item) => (
            <InstanceCard
              key={item.id}
              $active={item.id === selectedId}
              $rarity={item.graph.rarity}
              onClick={() => onSelect(item)}
            >
              <Popconfirm
                title="移除原料"
                description={`确定移除 ${item.graph.name || item.graph._id}？`}
                okText="移除"
                cancelText="取消"
                disabled={removeDisabled}
                onConfirm={(event) => {
                  event?.stopPropagation();
                  removeMaterialItem(item);
                }}
              >
                <InstanceRemoveControl
                  role="button"
                  aria-label="移除原料"
                  title={removeDisabled ? "当前不能移除" : "移除原料"}
                  $disabled={removeDisabled}
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <DeleteOutlined />
                </InstanceRemoveControl>
              </Popconfirm>
              <PropCard item={item} catalog={catalog} compact />
            </InstanceCard>
          ))}
        </ScrollableInstanceGrid>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无原料" />
      )}
    </SandboxSection>
  );

  function removeMaterialItem(item: RuntimePropInstance): void {
    if (!manager || removeDisabled || !item.alive) return;
    manager.removeProp(item);
    if (selectedId === item.id) onSelect(undefined);
  }
}

function isMaterialItem(item: RuntimePropInstance): boolean {
  return item.graph.type === 1;
}

function upsertItem(items: RuntimePropInstance[], item: RuntimePropInstance): RuntimePropInstance[] {
  const index = items.findIndex((current) => current.id === item.id);
  if (index < 0) return [...items, item];
  const next = [...items];
  next[index] = item;
  return next;
}

function removeItem(items: RuntimePropInstance[], id: string): RuntimePropInstance[] {
  return items.filter((item) => item.id !== id);
}
